import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "./api/axiosInstance";
import CountryCodeSelect from "./CountryCodeSelect";
import {
  PARTY_TYPES,
  TAX_ID_TYPES,
  countryDefaults,
  createBillToParty,
  isIndiaCountryName,
  updateBillToParty
} from "../services/billToService";
import { formatBusinessName, formatPascal } from "../utils/formatters";
import "../assets/css/ViewCustomer.css";

const EMPTY = {
  Party_Type: "Organization",
  Name: "",
  Contact_Person: "",
  Email: "",
  Country_Code: "+91",
  Phone: "",
  Address_Line_1: "",
  Address_Line_2: "",
  Country_Id: "",
  State_Id: "",
  City_Id: "",
  Zip_Code: "",
  GSTIN: "",
  PAN: "",
  Tax_Id_Type: "",
  Tax_Id: "",
  Notes: ""
};

function toForm(party) {
  if (!party) return EMPTY;
  return {
    Party_Type: party.Party_Type || "Organization",
    Name: party.Name || "",
    Contact_Person: party.Contact_Person || "",
    Email: party.Email || "",
    Country_Code: party.Country_Code || "+91",
    Phone: party.Phone || "",
    Address_Line_1: party.Address_Line_1 || "",
    Address_Line_2: party.Address_Line_2 || "",
    Country_Id: party.Country_Id != null ? String(party.Country_Id) : "",
    State_Id: party.State_Id != null ? String(party.State_Id) : "",
    City_Id: party.City_Id != null ? String(party.City_Id) : "",
    Zip_Code: party.Zip_Code || "",
    GSTIN: party.GSTIN || "",
    PAN: party.PAN || "",
    Tax_Id_Type: party.Tax_Id_Type || "",
    Tax_Id: party.Tax_Id || "",
    Notes: party.Notes || ""
  };
}

/**
 * Create / edit a Bill To party (company or individual) in a modal.
 *
 * @param {object} props
 * @param {object|null} [props.party] - existing party to edit; omit to create
 * @param {string|number} [props.setDefaultForClientId] - on create, also make it this tenant's default
 * @param {(party: object) => void} props.onSaved
 * @param {() => void} props.onClose
 * @param {number} [props.zIndex]
 */
function BillToPartyModal({ party = null, setDefaultForClientId, onSaved, onClose, zIndex = 1200 }) {
  const isEdit = !!party?.Bill_To_Id;
  const isSelf = !!party?.Is_Self;
  const [form, setForm] = useState(() => toForm(party));
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(toForm(party));
  }, [party]);

  useEffect(() => {
    axiosInstance
      .get("/countries")
      .then((res) => setCountries(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (!form.Country_Id) {
      setStates([]);
      return;
    }
    axiosInstance
      .get(`/states/${form.Country_Id}`)
      .then((res) => setStates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStates([]));
  }, [form.Country_Id]);

  useEffect(() => {
    if (!form.State_Id) {
      setCities([]);
      return;
    }
    axiosInstance
      .get(`/cities/${form.State_Id}`)
      .then((res) => setCities(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCities([]));
  }, [form.State_Id]);

  const countryName = useMemo(() => {
    const c = countries.find((x) => String(x.country_id) === String(form.Country_Id));
    return c?.country_name || "";
  }, [countries, form.Country_Id]);
  // No country yet -> assume India (the common case); otherwise GSTIN/PAN only make sense for India.
  const isIndia = !form.Country_Id || isIndiaCountryName(countryName);
  const showIndianTax = isIndia || !!form.GSTIN || !!form.PAN;
  const showIntlTax = !isIndia || !!form.Tax_Id;

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((p) => {
      const next = { ...p, [key]: value };
      if (key === "Country_Id") {
        next.State_Id = "";
        next.City_Id = "";
        const picked = countries.find((x) => String(x.country_id) === String(value));
        const defaults = countryDefaults(picked?.country_name);
        // Pre-fill dial code / tax-id type the first time; don't clobber values the user typed.
        if (!p.Phone.trim() && defaults.dialCode) next.Country_Code = defaults.dialCode;
        if (!p.Tax_Id.trim()) next.Tax_Id_Type = defaults.taxType;
      } else if (key === "State_Id") {
        next.City_Id = "";
      }
      return next;
    });
  };

  const isOrg = form.Party_Type === "Organization";
  const title = useMemo(() => {
    if (!isEdit) return "New Bill To";
    return isSelf ? "Edit own company Bill To" : "Edit Bill To";
  }, [isEdit, isSelf]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // React bubbles submit through portals, so the host client form would also submit.
    e.stopPropagation();
    if (!form.Name.trim()) {
      setError("Name is required.");
      return;
    }
    if (form.Tax_Id.trim() && !form.Tax_Id_Type) {
      setError("Choose the Tax ID type.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      Party_Type: form.Party_Type,
      Name: isOrg ? formatBusinessName(form.Name.trim()) : formatPascal(form.Name.trim()),
      Contact_Person: formatPascal(form.Contact_Person.trim()) || null,
      Email: form.Email.trim().toLowerCase() || null,
      Country_Code: form.Phone.trim() ? form.Country_Code.trim() || null : null,
      Phone: form.Phone.trim() || null,
      Address_Line_1: form.Address_Line_1.trim() || null,
      Address_Line_2: form.Address_Line_2.trim() || null,
      Country_Id: form.Country_Id ? Number(form.Country_Id) : null,
      State_Id: form.State_Id ? Number(form.State_Id) : null,
      City_Id: form.City_Id ? Number(form.City_Id) : null,
      Zip_Code: form.Zip_Code.trim() || null,
      GSTIN: form.GSTIN.trim().toUpperCase() || null,
      PAN: form.PAN.trim().toUpperCase() || null,
      Tax_Id_Type: form.Tax_Id.trim() ? form.Tax_Id_Type || null : null,
      Tax_Id: form.Tax_Id.trim().toUpperCase() || null,
      Notes: form.Notes.trim() || null
    };
    try {
      let saved;
      if (isEdit) {
        saved = await updateBillToParty(party.Bill_To_Id, payload);
      } else {
        if (setDefaultForClientId) payload.Set_Default_For_Client_Id = Number(setDefaultForClientId);
        saved = await createBillToParty(payload);
      }
      onSaved?.(saved);
    } catch (err) {
      console.error("Failed to save bill to party:", err);
      setError(err.response?.data?.error || err.response?.data?.details || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  // Portalled so the modal's <form> is not nested inside the Add / Edit client <form>.
  return createPortal(
    <div className="viewlead-modal-overlay" onClick={() => !saving && onClose?.()} style={{ zIndex }}>
      <div className="viewlead-modal-content tenant-invoices-modal" onClick={(e) => e.stopPropagation()}>
        <div className="viewlead-modal-header">
          <h3 className="viewlead-modal-title">{title}</h3>
          <button type="button" className="viewlead-modal-close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="tenant-invoices-modal-body">
          {isSelf ? (
            <p className="bill-to-modal-hint">
              Name, contact and address of your own company follow the client profile. Tax
              registrations (GSTIN / PAN or EIN / VAT etc.) are kept here.
            </p>
          ) : null}
          <div className="tenant-invoices-form-grid">
            <div className="form-group">
              <label>Type</label>
              <select value={form.Party_Type} onChange={set("Party_Type")} disabled={isSelf}>
                {PARTY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                {isOrg ? "Company name" : "Full name"} <span className="required">*</span>
              </label>
              <input type="text" value={form.Name} onChange={set("Name")} required disabled={isSelf} />
            </div>
            {isOrg ? (
              <div className="form-group">
                <label>Contact person</label>
                <input type="text" value={form.Contact_Person} onChange={set("Contact_Person")} disabled={isSelf} />
              </div>
            ) : null}
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.Email} onChange={set("Email")} disabled={isSelf} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <div className="bill-to-phone-row">
                <CountryCodeSelect value={form.Country_Code} onChange={set("Country_Code")} disabled={isSelf} />
                <input type="text" value={form.Phone} onChange={set("Phone")} disabled={isSelf} />
              </div>
            </div>
            <div className="form-group form-group-full">
              <label>Address line 1</label>
              <input type="text" value={form.Address_Line_1} onChange={set("Address_Line_1")} disabled={isSelf} />
            </div>
            <div className="form-group form-group-full">
              <label>Address line 2</label>
              <input type="text" value={form.Address_Line_2} onChange={set("Address_Line_2")} disabled={isSelf} />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select value={form.Country_Id} onChange={set("Country_Id")} disabled={isSelf}>
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c.country_id} value={c.country_id}>
                    {c.country_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>State</label>
              <select value={form.State_Id} onChange={set("State_Id")} disabled={isSelf || !form.Country_Id}>
                <option value="">Select state</option>
                {states.map((s) => (
                  <option key={s.state_id} value={s.state_id}>
                    {s.state_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select value={form.City_Id} onChange={set("City_Id")} disabled={isSelf || !form.State_Id}>
                <option value="">Select city</option>
                {cities.map((c) => (
                  <option key={c.city_id} value={c.city_id}>
                    {c.city_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{isIndia ? "PIN code" : "ZIP / Postal code"}</label>
              <input type="text" value={form.Zip_Code} onChange={set("Zip_Code")} disabled={isSelf} />
            </div>
            {showIndianTax ? (
              <>
                <div className="form-group">
                  <label>GSTIN</label>
                  <input
                    type="text"
                    value={form.GSTIN}
                    onChange={set("GSTIN")}
                    maxLength={15}
                    placeholder="36ABCDE1234F1Z5"
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
                <div className="form-group">
                  <label>PAN</label>
                  <input
                    type="text"
                    value={form.PAN}
                    onChange={set("PAN")}
                    maxLength={10}
                    placeholder="ABCDE1234F"
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
              </>
            ) : null}
            {showIntlTax ? (
              <>
                <div className="form-group">
                  <label>Tax ID type</label>
                  <select value={form.Tax_Id_Type} onChange={set("Tax_Id_Type")}>
                    <option value="">Select type</option>
                    {TAX_ID_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tax ID {form.Tax_Id_Type ? `(${form.Tax_Id_Type})` : ""}</label>
                  <input
                    type="text"
                    value={form.Tax_Id}
                    onChange={set("Tax_Id")}
                    maxLength={50}
                    placeholder={form.Tax_Id_Type === "EIN" ? "12-3456789" : form.Tax_Id_Type === "VAT" ? "GB123456789" : ""}
                    style={{ textTransform: "uppercase" }}
                  />
                  {!isIndia ? (
                    <span className="edittenant-field-hint">
                      Optional. Printed on the invoice under the Bill To block.
                    </span>
                  ) : null}
                </div>
              </>
            ) : null}
            <div className="form-group form-group-full">
              <label>Notes</label>
              <textarea rows={2} value={form.Notes} onChange={set("Notes")} />
            </div>
          </div>
          {error && <p className="tenant-invoices-error">{error}</p>}
          <div className="viewlead-modal-actions">
            <button type="button" className="btn-secondary-lead" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-edit-lead" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default BillToPartyModal;
